import { Box, CardActionArea, Typography } from "@mui/material";
import React from "react";
import styled from "@emotion/styled";
import { toCurrency } from "@helpers";
import { GainLossTypography, YTDPercents } from "@rentablo/components";
import { Card as CardOrg } from "@rentablo/components";
import { ProgressCircle } from "@rentablo/components";
import { AssetTypeCardLoading } from "./AssetTypeCardLoading";

const Marker = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`;

const PercentWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Card = styled(CardOrg, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>`
  flex-shrink: 0;
  border-radius: 8px;
  height: 120px;
  width: auto;
  min-width: 270px;
  ${({ active, theme }) =>
    active &&
    `
    background-color: ${theme.palette.background.selected};
    outline: 2px solid ${theme.palette.primary.main};
    outline-offset: -2px;
  `}
`;

const ActionArea = styled(CardActionArea)`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing(2, 3)};
`;

export interface Props {
  isFetching?: boolean;
  title: string;
  amount?: string;
  percent?: number;
  increase?: number;
  changeAbs?: number;
  color: string;
  currency?: string;
  // The card toggles the page's asset class filter.
  active?: boolean;
  onClick?: () => void;
}

export const AssetTypeCard = ({
  percent,
  onClick,
  active = false,
  title,
  amount,
  increase,
  changeAbs,
  color,
  currency = "EUR",
  isFetching = false,
}: Props) => {
  if (isFetching) {
    return <AssetTypeCardLoading />;
  }

  return (
    <Card size="micro" active={active}>
      <ActionArea
        aria-pressed={active}
        onClick={onClick}
        data-testid="asset-class-card"
      >
        <Box
          component="span"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          mb={4}
        >
          <Box component="span" display="flex" alignItems="center" gap={2}>
            <Marker color={color} />
            <Typography component="span" variant="body3" color="textSecondary">
              {title}
            </Typography>
          </Box>
          <PercentWrapper>
            {increase != undefined && <YTDPercents value={increase} />}
          </PercentWrapper>
        </Box>
        <Box component="span" display="flex" alignItems="center" gap={2}>
          <Box component="span" ml={-1}>
            {percent != undefined && <ProgressCircle percent={percent} />}
          </Box>
          <Box component="span">
            <Typography
              component="span"
              display="block"
              variant="h5"
              mb={1}
              sx={{ whiteSpace: "nowrap" }}
            >
              {amount}
            </Typography>
            {changeAbs != undefined && (
              <GainLossTypography
                component="span"
                value={changeAbs}
                variant="body4"
                showIndicator
              >
                {toCurrency(changeAbs, currency)}
              </GainLossTypography>
            )}
          </Box>
        </Box>
      </ActionArea>
    </Card>
  );
};
